import * as React from 'react';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, Typography } from '@mui/material';
import { importVirtualPlayers } from '../../Api';
import { VirtualPlayerImportResultDto } from '../../models/dtos/VirtualProviderCatalogDto';

const REQUIRED_HEADERS = [
    'sportId',
    'name',
    'nameRu',
    'description',
    'descriptionRu',
    'originCountry',
    'yearOfBirth',
    'eyeColor',
    'skinColor',
    'faceShape',
    'hairStyle',
    'strength',
    'technique',
    'intelligence',
    'svg',
];

export default function VirtualPlayerImportDialog(props: {
    open: boolean;
    onClose: () => void;
    onImported: (result: VirtualPlayerImportResultDto) => void;
}) {
    const [file, setFile] = React.useState<File | null>(null);
    const [csv, setCsv] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!props.open) return;
        setFile(null);
        setCsv('');
        setError('');
    }, [props.open]);

    const selectFile = async (selected: File | undefined) => {
        setFile(selected ?? null);
        setCsv('');
        setError('');
        if (!selected) return;
        if (!selected.name.toLowerCase().endsWith('.csv')) {
            setError('Select a CSV file.');
            return;
        }
        if (selected.size > 20_000_000) {
            setError('CSV files cannot exceed 20 MB.');
            return;
        }
        try {
            setCsv(await selected.text());
        } catch {
            setError('Could not read the selected CSV file.');
        }
    };

    const importPlayers = async () => {
        setBusy(true);
        setError('');
        try {
            const result = await importVirtualPlayers(csv);
            props.onImported(result);
            props.onClose();
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Player import failed');
        } finally {
            setBusy(false);
        }
    };

    return <Dialog open={props.open} onClose={busy ? undefined : props.onClose} fullWidth maxWidth="md">
        <DialogTitle>Import virtual players</DialogTitle>
        <DialogContent>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            <Stack spacing={2} sx={{ mt: 1 }}>
                <DialogContentText>
                    Upload a UTF-8 CSV containing all player fields. Descriptions and SVG values containing commas or line breaks must be enclosed in double quotes. Every row is validated before the import is committed.
                </DialogContentText>
                <Typography variant="caption" component="code" sx={{ overflowWrap: 'anywhere' }}>
                    {REQUIRED_HEADERS.join(',')}
                </Typography>
                <Button component="label" variant="outlined" disabled={busy} sx={{ alignSelf: 'flex-start' }}>
                    Select CSV file
                    <input
                        hidden
                        type="file"
                        accept=".csv,text/csv"
                        onChange={event => {
                            const selected = event.target.files?.[0];
                            event.target.value = '';
                            void selectFile(selected);
                        }}
                    />
                </Button>
                <Typography color={file ? 'text.primary' : 'text.secondary'}>
                    {file ? `${file.name} (${Math.ceil(file.size / 1024)} KB)` : 'No file selected'}
                </Typography>
                <DialogContentText>
                    The supplied SVG is sanitized, stored internally, and rendered to the 512×512 PNG served as the player portrait.
                </DialogContentText>
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button onClick={props.onClose} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={importPlayers} disabled={!csv.trim() || busy}>
                {busy ? <CircularProgress size={20} /> : 'Import players'}
            </Button>
        </DialogActions>
    </Dialog>;
}
